/**
 * Terrain Shader Code
 * 
 * GLSL shader snippets for terrain materials with tri-planar mapping.
 * Separated from TerrainMaterial.ts for better organization.
 */

// ============== Shared Shader Components ==============

/** Material attribute declarations (vertex) */
/**
 * Material id recovery. A shared vertex writes its id into ITS OWN slot only (MeshGeometry, the slot
 * rule), so the interpolated `vMaterialIds[k]` is id_k × w_k: a ratio of two values interpolated by
 * the same barycentric, constant across the triangle. Where a slot's weight is negligible the ratio
 * is noise — and the slot contributes nothing — so it takes the dominant slot's id, a real layer to
 * fetch from. Rounded: it indexes a texture array, and the dedupe compares it for equality.
 */
export const materialIdRecoveryGlsl = /* glsl */ `
  vec3 recoverMaterialIds(vec3 idw, vec3 w) {
    vec3 ids = idw / max(w, vec3(1e-4));
    float dom = (w.x >= w.y && w.x >= w.z) ? ids.x : (w.y >= w.z ? ids.y : ids.z);
    ids = mix(vec3(dom), ids, step(vec3(1e-3), w));
    return floor(ids + 0.5);
  }
`;

/**
 * The world-space pair. The TERRAIN shader no longer carries these: with ~9M non-indexed vertices a
 * frame the interpolants are a measurable share of the vertex cost, and both are recoverable in the
 * fragment from three's own view-space varyings (vViewPosition, vNormal) and the rigid viewMatrix —
 * see terrainDiffuseFragment. The water and depth shaders still carry them, and the held/preview
 * terrain variant (TERRAIN_OBJECT_SPACE) carries them holding OBJECT space, which the fragment
 * cannot reconstruct.
 */
const worldVaryingsDecl = /* glsl */ `
  varying vec3 vWorldPosition;
  varying vec3 vWorldNormal;
`;

const materialVaryingsOnly = /* glsl */ `
  vMaterialIds = materialIds;
  vMaterialWeights = materialWeights;
  vLightLevel = lightLevel;
  vBlockLight = blockLight;
  vSkirtKind = skirtKind;
`;

export const materialAttributesVertex = /* glsl */ `
  attribute vec3 materialIds;
  attribute vec3 materialWeights;
  attribute float lightLevel;
  attribute float blockLight;
  attribute float skirtKind;
  // How much of this vertex sits against opaque geometry (SurfaceNetOutput.shoreFade). The mesher
  // welds a layer's rim onto the solid beside it; displacing that rim lifts it back off, and the
  // ground it uncovers has no sheet over it — a gap of amplitude / gradient, so a metre on a shallow
  // shore. A vertex that touches the solid IS the seam and does not move. Declared here for the
  // water shader too, which concatenates this block. Always 0 on the solid layer, which never moves.
  attribute float shoreFade;

  varying vec3 vMaterialIds;   // id × slot weight per slot — see recoverMaterialIds
  varying vec3 vMaterialWeights;
  varying float vLightLevel;
  varying float vBlockLight;
  varying float vSkirtKind;
  ${worldVaryingsDecl}
`;

/**
 * Material varying assignments (vertex suffix) — the water and depth shaders concatenate this whole
 * block; the terrain shader uses the parts (see terrainVertexSuffix).
 */
/**
 * BATCHING. Terrain is drawn by THREE.BatchedMesh (TerrainBatch): positions are chunk-local and the
 * chunk's world offset is the per-geometry `batchingMatrix`, which three applies inside its own
 * chunks (project_vertex, worldpos_vertex, defaultnormal_vertex) but NOT in ours. Every custom
 * computation of a world position or of gl_Position goes through these, or the tri-planar mapping
 * samples the chunk-local position and every batched chunk shares one texture placement.
 */
export const batchedPositionGlsl = /* glsl */ `
  vec4 terrainLocalPos = vec4(position, 1.0);
  vec4 terrainLocalNrm = vec4(normal, 0.0);
  #ifdef USE_BATCHING
    terrainLocalPos = batchingMatrix * terrainLocalPos;
    terrainLocalNrm = batchingMatrix * terrainLocalNrm;
  #endif
`;

/**
 * SEAM SKIRT COLOURS FOR THE LOD VIEW, indexed by `skirtKind` (see seamField's KIND_*). Index 0 is
 * "not a seam face" and never drawn from here — the mesh keeps its own LOD colour, which is what
 * lets terrain omit the attribute entirely.
 *
 * PICKED AGAINST THE LOD WHEEL, not for prettiness. LOD_DEBUG_COLORS already occupies green 122,
 * orange 36, yellow 54, cyan 191, blue 207, violet 255 and magenta 291, and debugMode 13 paints
 * back-facing surfaces magenta 300 to mark a hole. A skirt hue within ~20 degrees of any of those is
 * unreadable in the only view it exists for: an amber seam face against LOD3 orange terrain, or a
 * violet one against LOD7, cannot be told from the ground it sits on. These sit in the three widest
 * gaps that leaves — rose 348, spring green 158 and chartreuse 84.
 *
 * The shading band in the fragment shader is the second half of this: a seam face is lit into
 * [0.78, 1] while terrain uses the full [0.35, 1], so a skirt reads as flat and bright even where
 * its hue is near its neighbour's. Hue alone was not enough.
 */
export const SKIRT_KIND_COLORS: readonly number[] = [
  0x000000, // 0 unused  — terrain keeps uLodColor
  0xff2a63, // 1 fan     — rose 348: two fine cells inside one coarse cell
  0x00ffa2, // 2 quad    — spring green 158: a fine pair straddling a coarse boundary
  0xa6ff1a, // 3 band    — chartreuse 84: the high side's cell-0 layer the mesher withheld
  0x1a9bff, // 4 lid     — azure 205: one side capped where the other level is not there
];

export const SKIRT_KIND_NAMES: readonly string[] = [
  '', 'fan', 'quad', 'band', 'lid',
];

/** Per-LOD colours for the F7 view, index = lod. Deliberately no red (skirts own it). */
export const LOD_DEBUG_COLORS: readonly number[] = [
  0x4caf50, // 0 green
  0x2196f3, // 1 blue
  0xffeb3b, // 2 yellow
  0xff9800, // 3 orange
  0xe040fb, // 4 magenta
  0x00e5ff, // 5 cyan
  0xffffff, // 6 white
  0x7c4dff, // 7 violet
];

const glslRgb = (hex: number): string => {
  const r = ((hex >> 16) & 0xff) / 255, g = ((hex >> 8) & 0xff) / 255, b = (hex & 0xff) / 255;
  return `vec3(${r.toFixed(4)}, ${g.toFixed(4)}, ${b.toFixed(4)})`;
};

/** The LOD palette as a GLSL if-chain over the vertex's level (mod 8, like lodDebugColor). */
const lodLevelSelectGlsl = LOD_DEBUG_COLORS
  .map((hex, i) => `      if (lodIdx == ${i}) lodCol = ${glslRgb(hex)};`)
  .join('\n');

/** The palette as a GLSL if-chain over `vSkirtKind`, so TS stays the single source of truth. */
const skirtKindSelectGlsl = SKIRT_KIND_COLORS
  .slice(1)
  .map((hex, i) => {
    const r = ((hex >> 16) & 0xff) / 255, g = ((hex >> 8) & 0xff) / 255, b = (hex & 0xff) / 255;
    const rgb = `vec3(${r.toFixed(4)}, ${g.toFixed(4)}, ${b.toFixed(4)})`;
    return `      if (vSkirtKind > ${(i + 0.5).toFixed(1)}) lodCol = ${rgb};`;
  })
  .join('\n');

/** Material varying assignments (vertex suffix) */
export const materialVaryingsSuffix = /* glsl */ `
  ${materialVaryingsOnly}
  ${batchedPositionGlsl}
  vWorldPosition = (modelMatrix * terrainLocalPos).xyz;
  vWorldNormal = normalize((modelMatrix * terrainLocalNrm).xyz);
`;

/** Wind animation code (vertex) - requires uniforms uTime, uWindStrength, uWindFrequency, and a
 *  `terrainWorldPos` local (terrainVertexSuffix). */
export const windAnimationVertex = /* glsl */ `
  // Wind animation - layered sine waves based on world position
  float windPhase = uTime + terrainWorldPos.x * uWindFrequency + terrainWorldPos.z * uWindFrequency * 0.7;
  float windX = sin(windPhase) * sin(windPhase * 0.4 + 1.3);
  float windY = sin(windPhase * 0.6 + 0.8) * sin(windPhase * 0.25) * 0.5;
  float windZ = sin(windPhase * 0.8 + 2.1) * sin(windPhase * 0.3);
  vec3 windOffset = vec3(windX, windY, windZ) * uWindStrength
    * (1.0 - step(0.05, shoreFade));
  vec4 windLocalPos = vec4(position + windOffset, 1.0);
  #ifdef USE_BATCHING
    windLocalPos = batchingMatrix * windLocalPos;
  #endif
  gl_Position = projectionMatrix * modelViewMatrix * windLocalPos;
`;

/** Wind uniform declarations */
export const windUniformsVertex = /* glsl */ `
  uniform float uTime;
  uniform float uWindStrength;
  uniform float uWindFrequency;
`;

// ============== Shared Fragment Utilities ==============

/** Common fragment varyings (must match vertex outputs) */
const fragmentVaryingsOnly = /* glsl */ `
  varying vec3 vMaterialIds;   // id × slot weight per slot — see recoverMaterialIds
  varying vec3 vMaterialWeights;
  varying float vLightLevel;
  varying float vBlockLight;
  varying float vSkirtKind;
`;

const fragmentVaryings = /* glsl */ `
  ${fragmentVaryingsOnly}
  ${worldVaryingsDecl}
`;

/** Tri-planar and material blending core functions */
const blendingFunctions = /* glsl */ `
  
  // HSV to RGB conversion for material ID coloring
  vec3 hsv2rgb(vec3 c) {
    vec4 K = vec4(1.0, 2.0/3.0, 1.0/3.0, 3.0);
    vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
    return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
  }
  
  // Get unique hue for material ID (using golden ratio for good distribution)
  float materialIdToHue(float id) {
    return fract(id * 0.618033988749895);
  }
  
  vec3 calcTriPlanarBlend(vec3 normal, mat3 offset) {
    vec3 blending = offset * normal;
    #ifdef TRIPLANAR_SHARPNESS_8
    // The default sharpness is 8 = 2^3: three squarings instead of a pow (exp2+log2) per component.
    blending = abs(blending); blending *= blending; blending *= blending; blending *= blending;
    #else
    blending = pow(abs(blending), vec3(blendSharpness));
    #endif
    blending = max(blending, 0.00001);
    return blending / (blending.x + blending.y + blending.z);
  }
  
  vec3 calcMaterialBlend(vec3 weights) {
    vec3 w = weights * weights;
    w = max(w, 0.00001);
    return w / (w.x + w.y + w.z);
  }
  
  vec4 sampleTriPlanarAt(sampler2DArray tex, vec3 pos, vec3 blend, float scale, float layer) {
    vec4 xaxis = texture(tex, vec3(pos.zy * scale, layer));
    vec4 yaxis = texture(tex, vec3(pos.xz * scale, layer));
    vec4 zaxis = texture(tex, vec3(pos.xy * scale, layer));
    return xaxis * blend.x + yaxis * blend.y + zaxis * blend.z;
  }
  
  vec4 sampleMaterialBlendAt(sampler2DArray tex, vec3 pos, vec3 triBlend, vec3 matBlend, vec3 matIds, float scale) {
    vec4 m0 = sampleTriPlanarAt(tex, pos, triBlend, scale, matIds.x);
    vec4 m1 = sampleTriPlanarAt(tex, pos, triBlend, scale, matIds.y);
    vec4 m2 = sampleTriPlanarAt(tex, pos, triBlend, scale, matIds.z);
    return m0 * matBlend.x + m1 * matBlend.y + m2 * matBlend.z;
  }
`;

// ============== TerrainMaterial Shaders ==============

export const terrainVertexPrefix = /* glsl */ `
  attribute vec3 materialIds;
  attribute vec3 materialWeights;
  attribute float lightLevel;
  attribute float blockLight;
  attribute float skirtKind;
  attribute float shoreFade;
  varying vec3 vMaterialIds;   // id × slot weight per slot — see recoverMaterialIds
  varying vec3 vMaterialWeights;
  varying float vLightLevel;
  varying float vBlockLight;
  varying float vSkirtKind;
  #ifdef TERRAIN_OBJECT_SPACE
  ${worldVaryingsDecl}
  #endif
  uniform float repeatScale;
  // Which LOD this vertex's chunk is (see lodTexScale) — a batch mixes levels. The enlargement
  // policy is in the uniforms, so retuning it costs a uniform write rather than a restamp.
  attribute float lodLevel;
  uniform float uTexScaleFirstLod;
  uniform float uTexScalePerLod;
  flat varying float vTexScaleK;
  flat varying float vLodLevel;
  
  #ifdef USE_WIND
  ${windUniformsVertex}
  #endif
`;

export const terrainVertexSuffix = /* glsl */ `
  ${materialVaryingsOnly}
  ${batchedPositionGlsl}
  vec3 terrainWorldPos = (modelMatrix * terrainLocalPos).xyz;
  #ifdef TERRAIN_OBJECT_SPACE
  // Held/preview item: the tri-planar mapping is OBJECT space — both the sample position and the
  // projection normal — so a camera-locked mesh's texture neither swims nor stretches as it turns.
  vWorldPosition = position;
  vWorldNormal = normalize(normal);
  #endif
  vTexScaleK = pow(uTexScalePerLod, -max(0.0, lodLevel - uTexScaleFirstLod + 1.0));
  vLodLevel = lodLevel;
  
  #ifdef USE_WIND
  ${windAnimationVertex}
  #endif
`;

/**
 * Declarations for the transparent variant's FOLIAGE_LIGHTING (see TerrainMaterial.foliageLightsFragment):
 * the two tunables and the hemisphere normal whose y is pulled toward |y| by the sky bias, so an
 * underside is weighted like a top face (sky colour) instead of like the ground.
 */
export const foliageLightingDecl = /* glsl */ `
  #ifdef FOLIAGE_LIGHTING
    uniform float uFoliageTranslucency;
    uniform float uFoliageSkyBias;
    vec3 foliageHemiNormal(vec3 n) {
      return normalize(vec3(n.x, mix(n.y, abs(n.y), uFoliageSkyBias), n.z));
    }
  #endif
`;

export const terrainFragmentPrefix = /* glsl */ `
  ${foliageLightingDecl}
  uniform sampler2DArray mapArray;
  uniform sampler2DArray normalArray;
  uniform sampler2DArray aoArray;
  uniform sampler2DArray roughnessArray;
  uniform sampler2DArray metalnessArray;
  uniform float repeatScale;
  uniform float repeatScales[64];
  flat varying float vTexScaleK;
  flat varying float vLodLevel;
  uniform mat3 blendOffset;
  uniform int debugMode;
  // LOD debug tint — per-MATERIAL, not per-mesh: the LOD view swaps in a material variant per LOD
  // (see LodDebugMaterials), because a terrain draw call is always single-LOD.
  uniform vec3 uLodColor;

  // Material adjustment uniforms
  uniform float roughnessMultiplier;
  uniform float metalnessOffset;
  uniform float aoIntensity;
  uniform float normalStrength;
  uniform float blendSharpness;
  
  // Voxel light fill curve exponents
  uniform float skyFillPower;    // sky-light channel (<1 lifts dark areas)
  uniform float blockFillPower;  // block-light channel (>1 = tighter drop-off)

  // Block-light (emitter) uniforms
  uniform vec3 uBlockLightColor;
  uniform float uBlockLightIntensity;

  ${fragmentVaryingsOnly}
  #ifdef TERRAIN_OBJECT_SPACE
  ${worldVaryingsDecl}
  #endif
  
  // Precomputed blend weights - set once in main(), reused everywhere
  vec3 gTriBlend;
  vec3 gMatBlend;
  vec3 gMatIds;   // the three slots' material ids, recovered (recoverMaterialIds)
  // Which of a triangle's three material slots carry the SAME material. The slots are the materials
  // of the triangle's three vertices (MeshGeometry), so on most triangles all three are one material
  // and the per-slot fetches would hit the same layer at the same UVs three times over (the repeat
  // scale is per material, so the UVs coincide too) and blend identical values with weights that sum
  // to 1. The recovered ids are constant across a triangle (and rounded), so the branches below never
  // diverge inside one. 0 = three distinct, 1 = all the same, 2 = x==y, 3 = x==z, 4 = y==z.
  int gMatDup;
  vec2 gTriUV_zy;
  vec2 gTriUV_xz;
  vec2 gTriUV_xy;
  // Per-material UVs for tri-planar sampling
  vec2 gTriUV_zy_m0, gTriUV_zy_m1, gTriUV_zy_m2;
  vec2 gTriUV_xz_m0, gTriUV_xz_m1, gTriUV_xz_m2;
  vec2 gTriUV_xy_m0, gTriUV_xy_m1, gTriUV_xy_m2;
  
  ${blendingFunctions}
  ${materialIdRecoveryGlsl}
  
  // Sample tri-planar with per-material repeat scale
  vec4 sampleTriPlanarMat(sampler2DArray tex, float layer, vec2 uv_zy, vec2 uv_xz, vec2 uv_xy) {
    vec4 xaxis = texture(tex, vec3(uv_zy, layer));
    vec4 yaxis = texture(tex, vec3(uv_xz, layer));
    vec4 zaxis = texture(tex, vec3(uv_xy, layer));
    return xaxis * gTriBlend.x + yaxis * gTriBlend.y + zaxis * gTriBlend.z;
  }
  
  // Convenience wrappers using precomputed globals (for backward compatibility)
  vec4 sampleTriPlanar(sampler2DArray tex, float layer) {
    vec4 xaxis = texture(tex, vec3(gTriUV_zy, layer));
    vec4 yaxis = texture(tex, vec3(gTriUV_xz, layer));
    vec4 zaxis = texture(tex, vec3(gTriUV_xy, layer));
    return xaxis * gTriBlend.x + yaxis * gTriBlend.y + zaxis * gTriBlend.z;
  }
  
  void classifyMaterialSlots() {
    bool xy = gMatIds.x == gMatIds.y;
    bool xz = gMatIds.x == gMatIds.z;
    bool yz = gMatIds.y == gMatIds.z;
    gMatDup = (xy && yz) ? 1 : xy ? 2 : xz ? 3 : yz ? 4 : 0;
  }
  
  // Fetch each DISTINCT material once and give it the summed weight of its slots: 3 fetches on a
  // one-material triangle, 6 on a two-material one, 9 only where three materials meet. Same result
  // as blending all three slots (the weights sum to 1), for a third of the texture bandwidth.
  // One exit: ANGLE's HLSL backend warns (X4000) on a function that returns from inside branches.
  vec4 sampleMaterialBlend(sampler2DArray tex) {
    vec4 m0 = sampleTriPlanarMat(tex, gMatIds.x, gTriUV_zy_m0, gTriUV_xz_m0, gTriUV_xy_m0);
    vec4 result = m0;
    if (gMatDup == 2) {
      vec4 m2 = sampleTriPlanarMat(tex, gMatIds.z, gTriUV_zy_m2, gTriUV_xz_m2, gTriUV_xy_m2);
      result = m0 * (gMatBlend.x + gMatBlend.y) + m2 * gMatBlend.z;
    } else if (gMatDup == 3) {
      vec4 m1 = sampleTriPlanarMat(tex, gMatIds.y, gTriUV_zy_m1, gTriUV_xz_m1, gTriUV_xy_m1);
      result = m0 * (gMatBlend.x + gMatBlend.z) + m1 * gMatBlend.y;
    } else if (gMatDup == 4) {
      vec4 m1 = sampleTriPlanarMat(tex, gMatIds.y, gTriUV_zy_m1, gTriUV_xz_m1, gTriUV_xy_m1);
      result = m0 * gMatBlend.x + m1 * (gMatBlend.y + gMatBlend.z);
    } else if (gMatDup != 1) {
      vec4 m1 = sampleTriPlanarMat(tex, gMatIds.y, gTriUV_zy_m1, gTriUV_xz_m1, gTriUV_xy_m1);
      vec4 m2 = sampleTriPlanarMat(tex, gMatIds.z, gTriUV_zy_m2, gTriUV_xz_m2, gTriUV_xy_m2);
      result = m0 * gMatBlend.x + m1 * gMatBlend.y + m2 * gMatBlend.z;
    }
    return result;
  }
  
  vec4 sampleAxisMaterialBlend(sampler2DArray tex, vec2 uv) {
    if (gMatDup == 1) return texture(tex, vec3(uv, gMatIds.x));
    vec4 m0 = texture(tex, vec3(uv, gMatIds.x));
    vec4 m1 = texture(tex, vec3(uv, gMatIds.y));
    vec4 m2 = texture(tex, vec3(uv, gMatIds.z));
    return m0 * gMatBlend.x + m1 * gMatBlend.y + m2 * gMatBlend.z;
  }
  
  // The three per-axis normal-map taps of one material (the normal path blends per axis, so it
  // cannot go through sampleMaterialBlend).
  void sampleNormalAxes(float layer, vec2 uv_zy, vec2 uv_xz, vec2 uv_xy, out vec3 nx, out vec3 ny, out vec3 nz) {
    nx = texture(normalArray, vec3(uv_zy, layer)).xyz * 2.0 - 1.0;
    ny = texture(normalArray, vec3(uv_xz, layer)).xyz * 2.0 - 1.0;
    nz = texture(normalArray, vec3(uv_xy, layer)).xyz * 2.0 - 1.0;
  }
`;

export const terrainDiffuseFragment = /* glsl */ `
  #ifndef TERRAIN_OBJECT_SPACE
  // World position and normal, RECOVERED rather than interpolated (see worldVaryingsDecl): the
  // viewMatrix is rigid, so its inverse is its transpose, and three already interpolates the
  // view-space position (vViewPosition = -mvPosition) and normal (vNormal). Named like the varyings
  // they replace so every chunk below reads them unchanged.
  mat3 viewToWorld = transpose(mat3(viewMatrix));
  vec3 vWorldPosition = cameraPosition + viewToWorld * (-vViewPosition);
  vec3 vWorldNormal = viewToWorld * normalize(vNormal);
  #endif

  // Precompute all blend weights and scaled UVs once
  vec3 triPos = vWorldPosition / 8.0;
  gTriBlend = calcTriPlanarBlend(vWorldNormal, blendOffset);
  gMatBlend = calcMaterialBlend(vMaterialWeights);
  gMatIds = recoverMaterialIds(vMaterialIds, vMaterialWeights);
  classifyMaterialSlots();
  
  // Global UVs with default repeat scale (for backward compatibility)
  gTriUV_zy = triPos.zy * repeatScale;
  gTriUV_xz = triPos.xz * repeatScale;
  gTriUV_xy = triPos.xy * repeatScale;
  
  // Per-material UVs with individual repeat scales
  int m0 = int(gMatIds.x);
  int m1 = int(gMatIds.y);
  int m2 = int(gMatIds.z);
  float scale0 = repeatScales[m0] * vTexScaleK;
  float scale1 = repeatScales[m1] * vTexScaleK;
  float scale2 = repeatScales[m2] * vTexScaleK;
  
  gTriUV_zy_m0 = triPos.zy * scale0;
  gTriUV_xz_m0 = triPos.xz * scale0;
  gTriUV_xy_m0 = triPos.xy * scale0;
  gTriUV_zy_m1 = triPos.zy * scale1;
  gTriUV_xz_m1 = triPos.xz * scale1;
  gTriUV_xy_m1 = triPos.xy * scale1;
  gTriUV_zy_m2 = triPos.zy * scale2;
  gTriUV_xz_m2 = triPos.xz * scale2;
  gTriUV_xy_m2 = triPos.xy * scale2;
  
  vec4 sampledAlbedo = sampleMaterialBlend(mapArray);
  // Already linear: the albedo array is uploaded as SRGB8_ALPHA8 (TerrainMaterial
  // createDataArrayTexture) and the sampler decodes — no per-fragment pow() curve.
  vec3 linearAlbedo = sampledAlbedo.rgb;
  
  // Debug mode 11: Material ID hue tinting (grayscale albedo + material hue)
  #ifdef TERRAIN_DEBUG
  if (debugMode == 11) {
    // Convert to grayscale (luminance)
    float luma = dot(linearAlbedo, vec3(0.299, 0.587, 0.114));
    // Get dominant material ID (highest weight)
    float dominantId = (gMatBlend.x >= gMatBlend.y && gMatBlend.x >= gMatBlend.z) ? gMatIds.x :
                       (gMatBlend.y >= gMatBlend.z) ? gMatIds.y : gMatIds.z;
    // Blend hues based on material weights for smooth transitions
    float blendedHue = materialIdToHue(gMatIds.x) * gMatBlend.x +
                       materialIdToHue(gMatIds.y) * gMatBlend.y +
                       materialIdToHue(gMatIds.z) * gMatBlend.z;
    // Apply hue tint: HSV with blended hue, medium saturation, albedo luminance as value
    linearAlbedo = hsv2rgb(vec3(blendedHue, 0.6, luma));
  }
  #endif
  
  #ifdef USE_TEXTURE_ALPHA
    vec4 diffuseColor = vec4(linearAlbedo, sampledAlbedo.a);
  #else
    vec4 diffuseColor = vec4(linearAlbedo, 1.0);
  #endif
`;

export const terrainRoughnessFragment = /* glsl */ `
  #ifdef QUALITY_METALNESS_MAPS
    float roughnessFactor = sampleMaterialBlend(roughnessArray).r * roughnessMultiplier;
  #else
    float roughnessFactor = roughnessMultiplier;
  #endif
`;

export const terrainMetalnessFragment = /* glsl */ `
  #ifdef QUALITY_METALNESS_MAPS
    float metalnessFactor = clamp(sampleMaterialBlend(metalnessArray).r + metalnessOffset, 0.0, 1.0);
  #else
    float metalnessFactor = 0.0;
  #endif
`;

/**
 * THE AO MAP DARKENS SUNLIGHT, NOT SHADE. three's textbook placement is `indirectDiffuse *= ao` —
 * occlusion of ambient light — but in a shadow the ambient IS all the light, so that placement takes
 * its bite out of exactly the areas that are already dark and does nothing visible where the sun
 * lands. High/Ultra (the presets with AO maps) read as "darker shadows than Medium" for it. Applied to
 * the direct term instead: crevices and the bases of things pick up the detail in the sun, and shade
 * keeps its full ambient. Not physical; wanted.
 */
export const terrainAoFragment = /* glsl */ `
  #ifdef QUALITY_AO_MAPS
    float ambientOcclusion = mix(1.0, sampleMaterialBlend(aoArray).r, aoIntensity);
    reflectedLight.directDiffuse *= ambientOcclusion;
  #endif
`;

export const terrainNormalFragment = /* glsl */ `
  #ifdef QUALITY_NORMAL_MAPS
    // Sample normals per-axis, per-material with individual repeat scales — once per DISTINCT
    // material (see gMatDup): a one-material triangle takes 3 taps, not 9.
    vec3 normalSampleX, normalSampleY, normalSampleZ;
    if (gMatDup == 1) {
      sampleNormalAxes(gMatIds.x, gTriUV_zy_m0, gTriUV_xz_m0, gTriUV_xy_m0, normalSampleX, normalSampleY, normalSampleZ);
    } else {
      vec3 nX0, nY0, nZ0, nX1, nY1, nZ1, nX2, nY2, nZ2;
      sampleNormalAxes(gMatIds.x, gTriUV_zy_m0, gTriUV_xz_m0, gTriUV_xy_m0, nX0, nY0, nZ0);
      sampleNormalAxes(gMatIds.y, gTriUV_zy_m1, gTriUV_xz_m1, gTriUV_xy_m1, nX1, nY1, nZ1);
      sampleNormalAxes(gMatIds.z, gTriUV_zy_m2, gTriUV_xz_m2, gTriUV_xy_m2, nX2, nY2, nZ2);
      // Blend materials per-axis
      normalSampleX = nX0 * gMatBlend.x + nX1 * gMatBlend.y + nX2 * gMatBlend.z;
      normalSampleY = nY0 * gMatBlend.x + nY1 * gMatBlend.y + nY2 * gMatBlend.z;
      normalSampleZ = nZ0 * gMatBlend.x + nZ1 * gMatBlend.y + nZ2 * gMatBlend.z;
    }
    
    // Negation was three's normalScale (-1,-1) — the convention the pallet's maps are authored in.
    normalSampleX.xy *= -normalStrength;
    normalSampleY.xy *= -normalStrength;
    normalSampleZ.xy *= -normalStrength;
    
    // The tri-planar UVs ARE world axes — the Y projection samples pos.xz — so the tangent frame is
    // known by construction and needs no screen-space derivatives to measure it. Deriving it from a
    // UV is what let per-material repeatScales corrupt the frame and seam every blended triangle;
    // nothing about tiling can reach it now. Whiteout blend, so a flat normal map returns gn exactly
    // rather than flattening the surface.
    vec3 gn = normalize(vWorldNormal);
    vec3 nX = vec3(normalSampleX.xy + gn.zy, abs(normalSampleX.z) * gn.x);
    vec3 nY = vec3(normalSampleY.xy + gn.xz, abs(normalSampleY.z) * gn.y);
    vec3 nZ = vec3(normalSampleZ.xy + gn.xy, abs(normalSampleZ.z) * gn.z);
    
    vec3 worldN = normalize(nX.zyx * gTriBlend.x + nY.xzy * gTriBlend.y + nZ.xyz * gTriBlend.z);
    normal = normalize(mat3(viewMatrix) * worldN);
  #else
    // Normal maps disabled by quality setting - use geometry normal
    normal = normalize(vNormal);
  #endif
`;

// ============== Voxel Light Attenuation ==============

/** Applied right before opaque_fragment — voxel light controls brightness.
 *  Two independent channels combined by per-channel max:
 *   - Sky term: PBR scaled by sky light (shadows/specular preserved, time-of-day driven).
 *   - Block term: warm emitter light, sun-independent, so lit areas glow at night / in caves.
 *  max() means the sun wins in daylight (look unchanged) and block wins only where the sky
 *  term is dim — never over-brightening surfaces lit by both. */
export const terrainLightFragment = /* glsl */ `
  // Sky term: scale PBR by sky light (preserves shadows + specular). Guard pow() —
  // pow(0, uniform) is undefined in GLSL and returns NaN on some GPUs.
  vec3 skyTerm = outgoingLight * pow(max(vLightLevel, 0.001), skyFillPower);
  // Block term: warm emitter light, independent of the sun, with its own drop-off curve.
  vec3 blockTerm = pow(max(vBlockLight, 0.001), blockFillPower) * uBlockLightColor * uBlockLightIntensity * diffuseColor.rgb;
  // Combine by per-channel max (sun-dominant in daylight, block-dominant in the dark).
  outgoingLight = max(skyTerm, blockTerm);

  #ifdef OPAQUE
  diffuseColor.a = 1.0;
  #endif

  #ifdef USE_TRANSMISSION
  diffuseColor.a *= material.transmissionAlpha;
  #endif

  gl_FragColor = vec4( outgoingLight, diffuseColor.a );
`;

// ============== Debug Modes ==============

export const terrainDebugFragment = /* glsl */ `
  #include <dithering_fragment>
  
  #ifdef TERRAIN_DEBUG
  if (debugMode > 0) {
    if (debugMode == 1) {
      // SKY_LIGHT: per-vertex sky light level as grayscale
      gl_FragColor = vec4(vec3(vLightLevel), 1.0);
    } else if (debugMode == 2) {
      gl_FragColor = vec4(sampleMaterialBlend(mapArray).rgb, 1.0);
    } else if (debugMode == 3) {
      gl_FragColor = vec4(sampleMaterialBlend(normalArray).xyz, 1.0);
    } else if (debugMode == 4) {
      float ao = sampleMaterialBlend(aoArray).r;
      gl_FragColor = vec4(ao, ao, ao, 1.0);
    } else if (debugMode == 5) {
      float r = sampleMaterialBlend(roughnessArray).r;
      gl_FragColor = vec4(r, r, r, 1.0);
    } else if (debugMode == 6) {
      float m = sampleMaterialBlend(metalnessArray).r;
      gl_FragColor = vec4(m, m, m, 1.0);
    } else if (debugMode == 7) {
      gl_FragColor = vec4(gTriBlend, 1.0);
    } else if (debugMode == 8) {
      float primaryId = gMatIds.x;
      vec3 hue = vec3(
        sin(primaryId * 0.4) * 0.5 + 0.5,
        sin(primaryId * 0.4 + 2.094) * 0.5 + 0.5,
        sin(primaryId * 0.4 + 4.188) * 0.5 + 0.5
      );
      gl_FragColor = vec4(hue, 1.0);
    } else if (debugMode == 9) {
      gl_FragColor = vec4(gMatBlend, 1.0);
    } else if (debugMode == 10) {
      gl_FragColor = vec4(vWorldNormal * 0.5 + 0.5, 1.0);
    } else if (debugMode == 12) {
      // BLOCK_LIGHT: per-vertex block (emitter) light level as grayscale — no sunlight
      gl_FragColor = vec4(vec3(vBlockLight), 1.0);
    } else if (debugMode == 13 && !gl_FrontFacing) {
      // A HOLE, UNAMBIGUOUSLY. The LOD view renders double-sided, so the only way to be looking at
      // the INSIDE of terrain is through a gap in whatever should have been in front of it. Magenta
      // rather than "sky or dark rock?", which is the judgement call that has cost us most of the
      // session. It also catches a seam triangle wound the wrong way: those used to be culled and
      // invisible, and now they announce themselves.
      gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0);
    } else if (debugMode == 13) {
      // LOD: flat per-LOD colour, shaded by a fixed key light. Unshaded, terrain reads as a
      // silhouette and the surface shape you turned the view on to judge disappears.
      float lodShade = 0.35 + 0.65 * max(dot(normalize(vWorldNormal), normalize(vec3(0.4, 1.0, 0.25))), 0.0);
      // THE VERTEX'S OWN LEVEL picks the colour (a batch mixes levels); uLodColor is only the skirt
      // family fallback. WHICH MECHANISM BUILT THIS SEAM FACE — one hue each, see SKIRT_KIND_COLORS.
      // Terrain carries no skirtKind attribute, so it reads 0 and keeps the palette.
      vec3 lodCol = uLodColor;
      int lodIdx = int(mod(vLodLevel + 0.5, 8.0));
${lodLevelSelectGlsl}
${skirtKindSelectGlsl}
      // A SEAM FACE IS LIT INTO A NARROW BRIGHT BAND, terrain into the full one. Hue alone could not
      // separate a strip from the ground it lies on — the LOD wheel is crowded — and a flat bright
      // ribbon against shaded terrain reads instantly whatever its hue. Narrow rather than unlit, so
      // the strip keeps enough shape to show a face pointing the wrong way.
      float seamShade = vSkirtKind > 0.5 ? mix(0.78, 1.0, lodShade) : lodShade;
      gl_FragColor = vec4(lodCol * seamShade, 1.0);
    } else if (debugMode == 14) {
      // SHADING_NORMAL: the normal lighting actually uses, back in world space. The only view that
      // shows a broken tangent frame — mode 3 is the raw texture, mode 10 the geometry normal.
      gl_FragColor = vec4(transpose(mat3(viewMatrix)) * normal * 0.5 + 0.5, 1.0);
    }
  }
  #endif
`;

// ============== Depth Material Shaders ==============

export const depthVertexPrefix = /* glsl */ `
  ${materialAttributesVertex}
  uniform float repeatScale;
  ${windUniformsVertex}
`;

export const depthVertexSuffix = /* glsl */ `
  ${materialVaryingsSuffix}
  
  // Wind animation - must match TerrainMaterial displacement. Only while the caster carries
  // detail (SHADOW_CASTER_DETAIL): the map is a 500 ms snapshot now, so an animated caster would
  // only jump between snapshots, and on the low tier the sway is sub-texel anyway.
  vec3 windOffset = vec3(0.0);
  #ifdef SHADOW_CASTER_DETAIL
    float windPhase = uTime + vWorldPosition.x * uWindFrequency + vWorldPosition.z * uWindFrequency * 0.7;
    float windX = sin(windPhase) * sin(windPhase * 0.4 + 1.3);
    float windY = sin(windPhase * 0.6 + 0.8) * sin(windPhase * 0.25) * 0.5;
    float windZ = sin(windPhase * 0.8 + 2.1) * sin(windPhase * 0.3);
    windOffset = vec3(windX, windY, windZ) * uWindStrength
      * (1.0 - step(0.05, shoreFade));
  #endif
  vec4 depthLocalPos = vec4(position + windOffset, 1.0);
  #ifdef USE_BATCHING
    depthLocalPos = batchingMatrix * depthLocalPos;
  #endif
  gl_Position = projectionMatrix * modelViewMatrix * depthLocalPos;
`;

export const depthFragmentPrefix = /* glsl */ `
  uniform sampler2DArray mapArray;
  uniform float repeatScales[64];
  uniform float alphaCutoff;
  
  ${fragmentVaryings}
  ${materialIdRecoveryGlsl}
`;

export const depthAlphaDiscard = /* glsl */ `
  // SHADOW_CASTER_DETAIL off = the leaf quads cast as solid cards. At the low tier's texel size
  // (11-22 cm over a 4-chunk radius) the cutouts do not resolve in the map anyway, so the albedo
  // fetch, the id recovery and the discard below buy nothing visible per shadow fragment.
  #ifdef SHADOW_CASTER_DETAIL
  // Simplified alpha test for shadow depth pass:
  // Sample only the dominant material on the dominant tri-planar axis.
  // Reduces 9 texture fetches (3 materials × 3 axes) to 1.
  vec3 pos = vWorldPosition / 8.0;
  vec3 absN = abs(vWorldNormal);

  // Pick dominant material (highest weight)
  vec3 gMatIds = recoverMaterialIds(vMaterialIds, vMaterialWeights);
  float domId = gMatIds.x;
  float domW  = vMaterialWeights.x;
  if (vMaterialWeights.y > domW) { domId = gMatIds.y; domW = vMaterialWeights.y; }
  if (vMaterialWeights.z > domW) { domId = gMatIds.z; }

  // Pick dominant axis and sample once
  float scale = repeatScales[int(domId)];
  vec2 uv;
  if (absN.x >= absN.y && absN.x >= absN.z) {
    uv = pos.zy * scale;
  } else if (absN.y >= absN.z) {
    uv = pos.xz * scale;
  } else {
    uv = pos.xy * scale;
  }
  float alpha = texture(mapArray, vec3(uv, domId)).a;

  if (alpha < alphaCutoff) {
    discard;
  }
  #endif
`;

